import {useEffect, useRef, useState} from 'react';
import styles from './BreakthroughRecovery.module.css';

// Timings match the CSS bar keyframe (osa-br-grow, 7s cycle):
//   0–14%   (0–980ms)    hold at start
//   14–50%  (980–3500ms) grow
//   50–86%  (3500–6020ms) hold at end
//   86–96%  (6020–6720ms) shrink
//   96–100% (6720–7000ms) reset
const CYCLE_MS = 7000;
const GROW_START_MS = 980;
const GROW_END_MS = 3500;
const HOLD_END_MS = 6020;
const RESET_END_MS = 6720;
const START_VALUE = 2.2;
const END_VALUE = 7.0;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const format = (v: number) => `${v.toFixed(1)}h`;

export const BreakthroughRecovery = () => {
  const [display, setDisplay] = useState(format(START_VALUE));
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay(format(END_VALUE));
      return;
    }

    // animStart anchors to mount so the JS counter cycles in lockstep
    // with the CSS bar keyframe (also starts on mount).
    const animStart = performance.now();

    const tick = (now: number) => {
      const t = (now - animStart) % CYCLE_MS;
      if (t < GROW_START_MS) {
        setDisplay(format(START_VALUE));
      } else if (t < GROW_END_MS) {
        const progress = (t - GROW_START_MS) / (GROW_END_MS - GROW_START_MS);
        const eased = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        setDisplay(format(START_VALUE + (END_VALUE - START_VALUE) * eased));
      } else if (t < HOLD_END_MS) {
        setDisplay(format(END_VALUE));
      } else if (t < RESET_END_MS) {
        const progress = (t - HOLD_END_MS) / (RESET_END_MS - HOLD_END_MS);
        setDisplay(format(END_VALUE - (END_VALUE - START_VALUE) * progress));
      } else {
        setDisplay(format(START_VALUE));
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <figure className={styles.figure} aria-label="Hours in bed compared to hours of recovery, today vs with treatment">
      <div className={`${styles.row} ${styles.inBed}`}>
        <span className={styles.label}>
          Hours
          <br />
          in bed
        </span>
        <div className={styles.track}>
          <div className={`${styles.fill} ${styles.fillNeutral}`} />
        </div>
        <span className={styles.value}>8.0h</span>
      </div>

      <div className={styles.gapRow}>
        <span className={styles.gapLine} />
        <span className={styles.gapText}>vs.</span>
        <span className={styles.gapLine} />
      </div>

      <div className={`${styles.row} ${styles.recovery}`}>
        <span className={styles.label}>
          Hours of
          <br />
          recovery
        </span>
        <div className={styles.track}>
          <div className={`${styles.fill} ${styles.fillTreated}`} />
        </div>
        <span className={`${styles.value} ${styles.valueTreated}`}>{display}</span>
      </div>

      <div className={styles.state}>
        <span className={`${styles.stateLabel} ${styles.stateToday}`}>TODAY · WHAT YOUR BODY GETS</span>
        <span className={`${styles.stateLabel} ${styles.stateTransition}`}>↗ TREATMENT WORKING</span>
        <span className={`${styles.stateLabel} ${styles.stateTreatment}`}>WITH TREATMENT · WHAT'S POSSIBLE</span>
      </div>
    </figure>
  );
};

export default BreakthroughRecovery;
